import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Session } from '../schemas/user.schema';

interface AuthState {
    session: Session | null;
    /** False until the first /me probe settles, so guards don't bounce on load. */
    isResolved: boolean;
    setSession: (session: Session) => void;
    clearSession: () => void;
    setResolved: (resolved: boolean) => void;
}

/**
 * Subscribe with a selector, always:
 *
 *   const session = useAuthStore((s) => s.session);
 *
 * Destructuring the store subscribes the component to every field and has
 * produced render loops in sibling projects. It is a correctness rule here.
 */
export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            session: null,
            isResolved: false,
            setSession: (session) => set({ session, isResolved: true }),
            clearSession: () => set({ session: null, isResolved: true }),
            setResolved: (isResolved) => set({ isResolved }),
        }),
        {
            name: 'ponyup-auth',
            // Only the identity survives a reload. Tokens live in httpOnly
            // cookies and must never be readable from JavaScript, and
            // isResolved has to start false so the app re-probes the session.
            partialize: (state) => ({ session: state.session }),
        },
    ),
);
